"use client";
import { useState, useEffect, useRef } from "react";

import { useRouter } from "next/navigation";
import { hooks, providers, apijs, utils } from "@lib/client";
import logjs from "@metrojs/logjs";
import Consts from "@common/config/consts";
import { dayjs } from "@lib/client";

// APIクライアントとログインスタンスの初期化
const log = new logjs("useOrderedCommon");

/**
 * 共通処理用のカスタムフック
 * @param {Object} initState - フォームの初期状態
 * @param {Object} state - 追加の状態
 * @param {Object} actions - アクション関数
 * @returns {Array} [form, formProps] - フォームデータとプロパティ
 */
export function useOrderedCommon() {
  const router = useRouter();

  /**
   * 日付形式チェック関数
   * PHPテンプレート: ckDate(datestr)
   * @param {string} datestr - チェック対象の日付文字列（YYYY/MM/DD形式）
   * @returns {boolean} 日付が有効な場合true、無効な場合false
   */
  const ckDate = (datestr) => {
    if (!datestr || typeof datestr !== "string") return false;
    // YYYY-MM-DD形式の場合は-を削除してYYYY/MM/DD形式に変換
    if (datestr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      datestr = datestr.replace(/-/g, "").replace(/(\d{4})(\d{2})(\d{2})/, "$1/$2/$3");
    }
    // 正規表現による書式チェック
    if (!datestr.match(/^\d{4}\/\d{2}\/\d{2}$/)) {
      log.debug("正規表現による書式チェック エラー datestr:", datestr);
      return false;
    }
    // dayjsを使用して日付検証
    const d = dayjs(datestr, "YYYY/MM/DD");
    if (!d.isValid()) return false;
    // 2016/12/28以降かチェック（PHPテンプレートの実装に基づく）　@@TODO 要チェック 意味がわからん
    const minDate = dayjs("2016/12/28", "YYYY/MM/DD");
    const isValidDate = d.isAfter(minDate) || d.isSame(minDate, "day");

    if (!isValidDate) {
      return false;
    }

    // PHPテンプレート: var today = new Date(requestlimit); var s_date = new Date('2016/12/28'); if(today < s_date)
    // 上記のチェックは、すでにisValidDateで実装済み
    return true;
  };

  /**
   * 価格チェック関数
   * @param {string} value - チェック対象の価格文字列
   * @returns {string|null} エラーメッセージ（エラー時）、null（正常時）
   */
  const validatePrice = (value) => {
    if (value === "" || value == null) {
      return "価格を入力して下さい";
    }
    if (typeof value !== "string" && typeof value !== "number") {
      return "価格は数値のみで入力してください。";
    }
    const valueStr = String(value);
    if (!valueStr.match(/^[0-9\-]+$/)) {
      return "価格は数値のみで入力してください。";
    }
    if (parseInt(valueStr) < 1) {
      return "価格は1円以上で入力してください。";
    }
    return null;
  };

  /**
   * 送料チェック関数
   * @param {string} value - チェック対象の送料文字列
   * @returns {string|null} エラーメッセージ（エラー時）、null（正常時）
   */
  const validateShippingFee = (value) => {
    if (value === "" || value == null) {
      return "送料を入力して下さい";
    }
    if (typeof value !== "string" && typeof value !== "number") {
      return "送料は数値のみで入力してください。";
    }
    const valueStr = String(value);
    if (!valueStr.match(/^[0-9\-]+$/)) {
      return "送料は数値のみで入力してください。";
    }
    return null;
  };

  /**
   * 発送可能日チェック関数
   * @param {string} value - チェック対象の発送可能日文字列
   * @returns {string|null} エラーメッセージ（エラー時）、null（正常時）
   */
  const validateShippingDate = (value) => {
    if (value == null || value === "" || value === "-1") {
      return "発送可能日を選択して下さい";
    }
    return null;
  };

  /**
   * 見積有効期限チェック関数
   * @param {string} value - チェック対象の見積有効期限文字列
   * @returns {string|null} エラーメッセージ（エラー時）、null（正常時）
   */
  const validateEstimateExpiry = (value) => {
    if (value == null || value === "") {
      return "見積有効期限を入力して下さい";
    }
    return null;
  };

  /**
   * 純正品番の半角英数記号・半角空白チェック関数
   * @param {string} value - チェック対象の純正品番文字列
   * @returns {string|null} エラーメッセージ（エラー時）、null（正常時）
   */
  const validatePartsJyunseiCode = (value) => {
    // undefined、null、空文字列の場合は必須チェックではないためnullを返す
    if (utils.isNullOrEmpty(value)) {
      return null;
    }
    // 文字列型でない場合はエラー
    if (typeof value !== "string") {
      return "純正品番は半角英数記号のみで入力してください。";
    }
    // if (!value.match(/^[a-zA-Z0-9!-/:-@¥[-`{-~]+$/)) {
    //   return "純正品番は半角英数記号のみで入力してください。";
    // }
    if (!value.match(/^[ -~]+$/)) {
      return "純正品番は半角英数記号のみで入力してください。";
    }
    return null;
  };

  /**
   * 部品カテゴリの必須チェック関数
   * @param {string} value - チェック対象の部品カテゴリID
   * @returns {string|null} エラーメッセージ（エラー時）、null（正常時）
   */
  const validatePartsKindID = (value) => {
    if (utils.isNullOrEmpty(value) || Number(value) == -1 || Number(value) == -2) {
      return "部品カテゴリを選択して下さい";
    }
    return null;
  };

  /**
   * HTMLタグ削除処理
   * #87 HTMLタグ消し処理（PHPの2012～2037行目の処理を反映）
   * partsDataとaddPartsDataのBikouフィールドからHTMLタグを削除
   * @param {Object} partsData - 部品データオブジェクト
   * @param {Array} addPartsData - 追加部品データ配列
   */
  const removeHtmlTagsFromBikou = (partsData, addPartsData) => {
    // partsDataのBikouフィールドからHTMLタグを削除
    if (partsData) {
      Object.keys(partsData).forEach((key) => {
        const partsArray = partsData[key];
        if (Array.isArray(partsArray)) {
          partsArray.forEach((part) => {
            if (part.Bikou !== undefined && part.Bikou !== null) {
              // HTMLタグを削除（strip_tags相当）
              part.Bikou = part.Bikou ? part.Bikou.replace(/<[^>]*>/g, "") : "";
            }
          });
        }
      });
    }

    // addPartsDataのBikouフィールドからHTMLタグを削除
    if (addPartsData && Array.isArray(addPartsData)) {
      addPartsData.forEach((addPart) => {
        if (addPart.Bikou !== undefined && addPart.Bikou !== null) {
          // HTMLタグを削除（strip_tags相当）
          addPart.Bikou = addPart.Bikou ? addPart.Bikou.replace(/<[^>]*>/g, "") : "";
        }
      });
    }
  };

  /**
   * 部品情報のバリデーション関数
   * PHPテンプレート: confmParts()
   * @param {Object} partsData - バリデーション対象の部品データ
   * @param {Object} orderedDetailData - 注文詳細データ（GenshaID、partsListを含む）
   * @param {Object} actions - アクション関数（showAlertを含む）
   * @returns {number} バリデーションOKの部品数、エラー時は-1
   */
  const validatePartsData = (partsData, orderedDetailData, actions) => {
    const genshaID = orderedDetailData?.request?.GenshaID;

    const partsList = orderedDetailData?.partsList || [];
    log.debug("validatePartsData partsList:", partsList);
    log.debug("validatePartsData partsData:", partsData);
    const max = genshaID ? 1 : partsList.length;

    let str = "";
    let pname = "";
    let reccnt = 0;

    const isEmptyParts = (_v) => {
      return utils.isNullOrEmpty(_v);
    };

    for (let _row of partsList) {
      log.debug("validatePartsData _row:", _row);
      const { id } = _row;
      const pname = _row.P_PartsName;
      let _parts = [];
      if (utils.hasOwn(partsData, "add_parts_id-" + id)) {
        _parts = partsData["add_parts_id-" + id] || [];
      } else {
        _parts = partsData[id] || [];
      }
      log.debug("validatePartsData _parts:", _parts);
      for (let i = 0; i < _parts.length; i++) {
        const _part = _parts[i];
        log.debug("validatePartsData _part:", _part);
        const { PartsJyunseiCode, PartsKindID, StanderdPrice, Soryo, Hasso, Sell_Limit, CollorNo, ColorName, Bikou, JishaNo, PrepKind } = _part;
        let shitei_flg = 0; // 在庫なしフラグ
        if (!genshaID) {
          log.debug(`validatePartsData JishaNo:${JishaNo} isEmptyParts(JishaNo):`, isEmptyParts(JishaNo));
          log.debug(`validatePartsData PartsKindID:${PartsKindID} isEmptyParts(PartsKindID):`, isEmptyParts(PartsKindID));
          log.debug(`validatePartsData PartsJyunseiCode:${PartsJyunseiCode} isEmptyParts(PartsJyunseiCode):`, isEmptyParts(PartsJyunseiCode));
          log.debug(`validatePartsData StanderdPrice:${StanderdPrice} isEmptyParts(StanderdPrice):`, isEmptyParts(StanderdPrice));
          log.debug(`validatePartsData Soryo:${Soryo} isEmptyParts(Soryo):`, isEmptyParts(Soryo));
          log.debug(`validatePartsData Hasso:${Hasso} isEmptyParts(Hasso):`, isEmptyParts(Hasso));
          log.debug(`validatePartsData Sell_Limit:${Sell_Limit} isEmptyParts(Sell_Limit):`, isEmptyParts(Sell_Limit));
          log.debug(`validatePartsData Bikou:${Bikou} isEmptyParts(Bikou):`, isEmptyParts(Bikou));
          if (
            isEmptyParts(JishaNo) &&
            Number(PartsKindID) !== -1 &&
            Number(PartsKindID) !== -2 &&
            isEmptyParts(PartsKindID) &&
            isEmptyParts(PartsJyunseiCode) &&
            isEmptyParts(StanderdPrice) &&
            isEmptyParts(Soryo) &&
            (isEmptyParts(Hasso) || Hasso === "-1") &&
            isEmptyParts(Sell_Limit) &&
            isEmptyParts(Bikou)
          ) {
            log.debug("------------------- validatePartsData continue");
            continue;
          }
        } else {
          if (StanderdPrice === "" && Soryo === "" && Hasso === "" && Sell_Limit === "" && Bikou === "") {
            continue;
          }
        }
        log.debug(`validatePartsData no continue -------------- ${i}`);
        if (!genshaID) {
          // 自社管理番号が入力されている場合、PrepKindの選択チェック
          /* @@TODO 要チェック 選択できないので一旦コメントアウト
          if (JishaNo !== "" && PrepKind !== "1" && PrepKind !== "2") {
            str = "自社品または他社品を選択して下さい";
            break;
          }
          */
          // 純正品番の半角英数記号チェック
          const partsJyunseiCodeError = validatePartsJyunseiCode(PartsJyunseiCode);
          if (partsJyunseiCodeError) {
            log.debug(`validatePartsData PartsJyunseiCode:${PartsJyunseiCode} partsJyunseiCodeError:`, partsJyunseiCodeError);
            str = partsJyunseiCodeError;
            break;
          }
          // 部品カテゴリの必須チェック
          const partsKindIDError = validatePartsKindID(PartsKindID);
          if (partsKindIDError) {
            log.debug(`validatePartsData PartsKindID:${PartsKindID} partsKindIDError:`, partsKindIDError);
            str = partsKindIDError;
            break;
          }
        }

        // GenshaIDがある場合、在庫なし回答のチェック
        if (genshaID) {
          if (i === 0) {
            /* @@TODO 要チェック 在庫なし回答のチェック */
            const zaikonashiEl = document.getElementById(`zaikonashi_${i}_${j}`);
            if (zaikonashiEl && zaikonashiEl.checked) {
              const zaikonashi = zaikonashiEl.value || "0";
              if (zaikonashi === "1") {
                shitei_flg = 1;
              }
            }
          }
        }

        if (shitei_flg === 0) {
          log.debug(`在庫なし回答でない場合のバリデーション`);
          // 価格チェック
          const priceError = validatePrice(StanderdPrice);
          if (priceError) {
            log.debug(`validatePartsData StanderdPrice:${StanderdPrice} priceError:`, priceError);
            str = priceError;
            break;
          }
          // 送料チェック
          const shippingFeeError = validateShippingFee(Soryo);
          if (shippingFeeError) {
            log.debug(`validatePartsData Soryo:${Soryo} shippingFeeError:`, shippingFeeError);
            str = shippingFeeError;
            break;
          }
          // 発送可能日チェック
          const shippingDateError = validateShippingDate(Hasso);
          if (shippingDateError) {
            log.debug(`validatePartsData Hasso:${Hasso} shippingDateError:`, shippingDateError);
            str = shippingDateError;
            break;
          }
          // 見積有効期限チェック
          const estimateExpiryError = validateEstimateExpiry(Sell_Limit);
          if (estimateExpiryError) {
            log.debug(`validatePartsData Sell_Limit:${Sell_Limit} estimateExpiryError:`, estimateExpiryError);
            str = estimateExpiryError;
            break;
          }
          /* @@TODO 要チェック エラーになるので一旦コメントアウト
          if (!ckDate(Sell_Limit)) {
            str = "見積有効期限の日付を正しく入力してください。";
            break;
          }
            */
        }
        // バリデーション成功した場合のみカウント
        reccnt++;
      }
      if (str !== "") {
        actions.showAlert(`
          <p>
            部品名：${pname}
            <br />
            ${str}
          </p>
        `);
        return -1;
      }
    }

    return reccnt;
  };

  /**
   * 追加商品情報のバリデーション関数
   * PHPテンプレート: confmAddItem()
   * @param {Array} addPartsData - バリデーション対象の追加商品データ
   * @param {Object} orderedDetailData - 注文詳細データ（GenshaIDを含む）
   * @param {Object} actions - アクション関数（showAlertを含む）
   * @returns {number} バリデーションOKの追加商品数、エラー時は-1
   */
  const validateAdditionalPartsData = (addPartsData, orderedDetailData, actions) => {
    log.debug("validateAdditionalPartsData addPartsData:", addPartsData);
    const genshaID = orderedDetailData?.request?.GenshaID;
    const addPartsList = addPartsData || [];
    const rowcnt = genshaID ? Math.max(0, addPartsList.length - 1) : addPartsList.length;

    let str = "";
    let reccnt = 0;
    let errorRecordNo = 0; // エラー検出時のレコード番号を保存
    const max = genshaID ? rowcnt + 1 : rowcnt;

    for (let i = 0; i <= max; i++) {
      const _addParts = addPartsData[i];
      log.debug("validateAdditionalPartsData _addParts:", _addParts);
      // 入力値取得（DOMから取得）

      const addbunrui = _addParts?.selectedBunrui;
      const addpcode = _addParts?.selectedBui;
      const addpname = _addParts?.selectedParts;

      const addpsonota = _addParts?.OthPartsName;

      const addjishano = _addParts?.JishaNo;
      const addprepkind = _addParts?.PrepKind;
      const addjyunseicd = _addParts?.PartsJyunseiCode;
      const addsuryo = _addParts?.Soryo;
      const addpartskind = _addParts?.PartsKindID;
      const addprice = _addParts?.StanderdPrice;
      const adddelprice = _addParts?.Soryo;
      const addhasso_status = _addParts?.Hasso;
      const addrequestlimit = _addParts?.Sell_Limit;
      const addbiko = _addParts?.Bikou;

      // addpnameが存在しない場合はスキップ（取消された行）
      if (!_addParts) {
        continue;
      }

      // 入力チェック（すべて空欄の場合はスキップ）
      if (genshaID) {
        if (
          addbunrui === "" &&
          addpname === "" &&
          addjishano === "" &&
          addjyunseicd === "" &&
          addpartskind === "" &&
          addprice === "" &&
          adddelprice === "" &&
          addhasso_status === "" &&
          addrequestlimit === "" &&
          addbiko === ""
        ) {
          continue;
        }
      } else {
        if (
          addpname === "" &&
          addjishano === "" &&
          addjyunseicd === "" &&
          addpartskind === "" &&
          addprice === "" &&
          adddelprice === "" &&
          addhasso_status === "" &&
          addrequestlimit === "" &&
          addbiko === ""
        ) {
          continue;
        }
      }

      // バリデーション
      if (genshaID) {
        // 分類の必須チェック
        if (addbunrui === "") {
          errorRecordNo = genshaID ? i + 1 : i;
          str = "分類を選択して下さい";
          break;
        }
        // 部品名の必須チェック（addpsonota）
        if (addpsonota === "") {
          errorRecordNo = genshaID ? i + 1 : i;
          str = "部品名を入力して下さい";
          break;
        }
      } else {
        // 部品名の必須チェック
        if (addpname === "") {
          errorRecordNo = genshaID ? i + 1 : i;
          str = "部品名を選択して下さい";
          break;
        }
        // その他部品（addpcode末尾が'990'）の場合、部品名入力の必須チェック
        if (addpcode && addpcode.substr(addpcode.length - 3) === "990") {
          if (addpsonota === "") {
            errorRecordNo = genshaID ? i + 1 : i;
            str = "部品名を入力して下さい";
            break;
          }
        }
      }

      // 部品種別のチェック
      /* @@TODO 要チェック 選択できないので一旦コメントアウト
      if (addjishano !== "" && addprepkind !== "1" && addprepkind !== "2") {
        errorRecordNo = genshaID ? i + 1 : i;
        str = "自社品または他社品を選択して下さい";
        break;
      }
      */
      // 純正品番の半角英数記号チェック
      const addPartsJyunseiCodeError = validatePartsJyunseiCode(addjyunseicd);
      if (addPartsJyunseiCodeError) {
        errorRecordNo = genshaID ? i + 1 : i;
        str = addPartsJyunseiCodeError;
        break;
      }
      // 数量チェック
      if (addsuryo !== "" && addsuryo != null && !String(addsuryo).match(/^[0-9\-]+$/)) {
        errorRecordNo = genshaID ? i + 1 : i;
        str = "数量は数値のみで入力してください。";
        break;
      }
      // 部品カテゴリの必須チェック
      const addPartsKindIDError = validatePartsKindID(addpartskind);
      if (addPartsKindIDError) {
        errorRecordNo = genshaID ? i + 1 : i;
        str = addPartsKindIDError;
        break;
      }
      // 価格チェック
      const priceError = validatePrice(addprice);
      if (priceError) {
        errorRecordNo = genshaID ? i + 1 : i;
        str = priceError;
        break;
      }
      // 送料チェック
      const shippingFeeError = validateShippingFee(adddelprice);
      if (shippingFeeError) {
        errorRecordNo = genshaID ? i + 1 : i;
        str = shippingFeeError;
        break;
      }
      // 発送可能日チェック
      const shippingDateError = validateShippingDate(addhasso_status);
      if (shippingDateError) {
        errorRecordNo = genshaID ? i + 1 : i;
        str = shippingDateError;
        break;
      }
      // 見積有効期限チェック
      const estimateExpiryError = validateEstimateExpiry(addrequestlimit);

      if (estimateExpiryError) {
        errorRecordNo = genshaID ? i + 1 : i;
        str = estimateExpiryError;
        break;
      }
      if (!ckDate(addrequestlimit)) {
        log.debug("validateAdditionalPartsData addrequestlimit:", addrequestlimit);
        errorRecordNo = genshaID ? i + 1 : i;
        str = "見積有効期限の日付を正しく入力してください。";
        break;
      }

      reccnt++;
    }

    if (str !== "") {
      // PHPテンプレート: rcnt = genshaID ? i + 1 : i;
      // errorRecordNoはループ内でエラー検出時に設定済み
      actions.showAlert(str + "\n追加商品情報 レコード番号" + errorRecordNo);
      return -1;
    }
    return reccnt;
  };

  return {
    ckDate,
    validatePrice,
    validateShippingFee,
    validateShippingDate,
    validateEstimateExpiry,
    validatePartsJyunseiCode,
    validatePartsKindID,
    removeHtmlTagsFromBikou,
    validatePartsData,
    validateAdditionalPartsData,
  };
}
